import { Link, useParams } from "react-router-dom";
import { MapPin, Check, ArrowRight } from "lucide-react";
import { Seo, faqJsonLd, breadcrumbJsonLd } from "../components/Seo";
import { ServiceIcon } from "../components/ServiceIcon";
import { Faq } from "../components/Faq";
import { CallCta } from "../components/CallCta";
import { Testimonials } from "../components/Testimonials";
import { getCity, getRegion, nearbyCities, SERVICES, BUSINESS } from "../data/site";
import NotFound from "./NotFound";

const CITY_IMGS = [
  "https://images.unsplash.com/photo-1591658523269-4f48b4210c58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxXaW5uaXBlZyUyME1hbml0b2JhJTIwY2l0eXNjYXBlJTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3OTIzNzQyNzZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1632455351235-682d08cbb3e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxXaW5uaXBlZyUyME1hbml0b2JhJTIwY2l0eXNjYXBlJTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3OTIzNzQyNzZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1591658522986-9eb791d2a89a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxXaW5uaXBlZyUyME1hbml0b2JhJTIwY2l0eXNjYXBlJTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3OTIzNzQyNzZ8MA&ixlib=rb-4.1.0&q=85",
];

export default function CityPage() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <NotFound />;
  const nearby = nearbyCities(city.slug);
  const region = getRegion(city.slug);
  const img = CITY_IMGS[city.slug.length % CITY_IMGS.length];
  const faqs = [
    { q: `Do you provide 24/7 security guards in ${city.name}?`, a: `Yes. Our dispatch operates around the clock, and we staff 24/7 guarding, overnight shifts, and rapid alarm response in ${city.name} and the surrounding ${city.region} region.` },
    { q: `What security services are available in ${city.name}, Manitoba?`, a: `All of our services are available in ${city.name}: uniformed security guards, mobile patrol security, construction site security, event security, fire watch, concierge security, retail loss prevention, industrial security, healthcare security, executive protection, alarm response, and Indigenous community security.` },
    { q: `How do I hire a security guard company in ${city.name}?`, a: `Call ${BUSINESS.phone} or email ${BUSINESS.email}. Tell us about your ${city.name} site and we'll recommend coverage and provide a no-obligation quote.` },
  ];

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Seo
        title={`Security Guards ${city.name} MB | 1st Nations Security`}
        description={`Licensed security guard services in ${city.name}, Manitoba — uniformed guards, mobile patrols, event & construction site security. 24/7: ${BUSINESS.phone}.`}
        path={`/security-guards/${city.slug}/`}
        jsonLd={[faqJsonLd(faqs), breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: city.name, path: `/security-guards/${city.slug}` }])]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={img} alt={`${city.name}, Manitoba — security guard service area`} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0D0E11]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF9100]" data-testid="breadcrumb-home">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/service-areas" className="hover:text-[#FF9100]" data-testid="breadcrumb-areas">Service Areas</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">{city.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5 mb-5">
            <MapPin className="w-4 h-4 text-[#FF9100]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100]">{city.region} · Pop. {city.pop}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 max-w-3xl">
            Security Guard Services in <span className="text-[#FF9100]">{city.name}</span>, Manitoba
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mb-4">
            {city.desc} 1st Nations Security is an Indigenous-owned security guard company delivering licensed, professional protection in {city.name} — backed by 24/7 dispatch and province-wide supervision.
          </p>
          <p className="text-gray-400 leading-relaxed max-w-3xl">{city.local}</p>
        </div>
      </section>

      <section className="py-16" data-testid="city-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
            Security Services Available in {city.name}
          </h2>
          <p className="text-gray-400 max-w-3xl mb-8 leading-relaxed">
            Whether you need construction site security in {city.name}, mobile patrol security across the {city.region} region, event security for {city.name} festivals and gatherings, or fire watch services for a building system outage, one call covers it all.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/security-services/${s.slug}`}
                data-testid={`city-service-${s.slug}`}
                className="group flex items-center gap-4 bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-xl p-4 transition-all duration-200"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100]">
                  <ServiceIcon name={s.icon} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white group-hover:text-[#FF9100] transition-colors">{s.title} {city.name}</p>
                  <p className="text-xs text-gray-500">{s.category}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A0B0E]" data-testid="city-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">
              Why {city.name} Chooses 1st Nations Security
            </h2>
            <ul className="space-y-4">
              {[
                `Manitoba-licensed security guards with site-specific training for ${city.name} properties`,
                "Indigenous-owned and community dedicated — local hiring and culturally grounded service",
                "24/7 dispatch line with mobile patrol and alarm response capability",
                "GPS-verified patrols and digital incident reporting after every shift",
                `Flexible coverage — from one-night event security in ${city.name} to permanent posted guards`,
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-300" data-testid={`city-why-${i}`}>
                  <Check className="w-5 h-5 text-[#FF9100] mt-0.5 shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">
              Nearby Communities We Serve
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              In addition to {city.name}, we provide security guard services throughout the {city.region} region and across Manitoba.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/security-guards/${n.slug}`}
                  data-testid={`nearby-city-${n.slug}`}
                  className="group flex items-center justify-between bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-lg px-4 py-3 transition-colors duration-200"
                >
                  <span className="text-sm text-gray-300 group-hover:text-[#FF9100] transition-colors">Security Guards {n.name}</span>
                  <ArrowRight className="w-4 h-4 text-[#FF9100] opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {region && (
        <section className="py-16" data-testid="city-region-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-5">
              {region.title}: Our Security Coverage
            </h2>
            <p className="text-gray-400 leading-relaxed">{region.body}</p>
          </div>
        </section>
      )}

      <section className="py-16" data-testid="city-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8">
            {city.name} Security Services — FAQs
          </h2>
          <Faq items={faqs} testidPrefix={`${city.slug}-faq`} />
        </div>
      </section>

      <Testimonials />
      <CallCta heading={`Hire Security Guards in ${city.name} Today`} sub={`Call ${BUSINESS.phone} or email ${BUSINESS.email} — serving ${city.name} and all of the ${city.region} region, 24/7.`} testid="city-cta" />
    </div>
  );
}
